import { EventType } from "@/analytics/analytics-events";
import {
  HideCadElementEventProperties,
  IsolateCadElementEventProperties,
  SelectCadParentElementEventProperties,
} from "@/analytics/cad-bim-events-properties";
import {
  CopyCoordinateProperties,
  PlacePinProperties,
} from "@/analytics/place-pin-events-properties";
import {
  PlacePinEvent,
  SceneContextMenuEvent,
  useSceneEvents,
} from "@/components/common/scene-events-context";
import { WalkDepthLoadingToast } from "@/modes/walk-mode/walk-depth-loading-toast";
import { setPinMarker } from "@/store/pin-marker-slice";
import { useAppDispatch } from "@/store/store-hooks";
import {
  FaroMenu,
  FaroMenuItem,
  FaroMenuProps,
  FolderSmallIcon,
  NonVisibleIcon,
  PinIcon,
  useToast,
  useTypedEvent,
  VisibleIcon,
  WorldCoordinateIcon,
} from "@faro-lotv/app-component-toolbox";
import { Analytics } from "@faro-lotv/foreign-observers";
import { DistanceUnitTypes } from "@faro-lotv/foundation";
import { ClickAwayListener } from "@mui/material";
import {
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
  type JSX,
} from "react";
import { Vector3Tuple } from "three";
import { formatDistanceDecimal } from "../common/settings-context/distance-unit-utils";
import { useDistanceUnit } from "../common/settings-context/settings-context";
import { PanoNoDepthInfoToast } from "./pano-no-depth-info-toast";

/**
 * Get the coordinates string "x, y, z" in Z-up for a given point in Y-up
 *
 * @param point the given coordinate in Y-up
 * @param distanceUnit The unit of distance to use for formatting.
 * @param withUnitSymbol Whether to include the unit symbol (and a space after the separators)
 * in the output. Only applied when `distanceUnit` is defined. The general rule is to include
 * unit symbol for UI display, and no unit symbol and no space after separator for copy/paste
 * to other applications.
 * @returns a text string of the coordinates in z-up.
 */
export function getCoordinateStringZUp(
  point: Vector3Tuple,
  distanceUnit: DistanceUnitTypes,
  withUnitSymbol: boolean,
): string {
  const [x, y, z] = [point[0], -point[2], point[1]];
  const coordinateUnit = getCoordinateUnit(distanceUnit);
  // For UI display, add space after comma to avoid too dense with the units
  // For copy/paste, no space after comma, this is required for allowing to paste in AutoCAD using the "L" command.
  const delimiter = withUnitSymbol ? ",\u00A0" : ",";
  return `${formatDistanceDecimal(x, coordinateUnit, withUnitSymbol)}${delimiter}${formatDistanceDecimal(y, coordinateUnit, withUnitSymbol)}${delimiter}${formatDistanceDecimal(z, coordinateUnit, withUnitSymbol)}`;
}

/**
 * Formats a comma-separated coordinate string (e.g., "4550737.366,25645456.637,43.140")
 * into a labeled and readable format with thin and medium spaces.
 *
 * Example: "X: 4 550 737.366   Y: 25 645 456.637   Z: 43.140"
 *
 * @param point the given coordinate in Y-up
 * @param distanceUnit The unit of distance to use for formatting.
 * @returns The formatted coordinate string with labels and digit grouping.
 */
export function formatSparseCoordinateString(
  point: Vector3Tuple,
  distanceUnit: DistanceUnitTypes,
): string {
  const labels = ["X", "Y", "Z"];
  const coords = [point[0], -point[2], point[1]];

  // Narrow no-break space around colon
  const labelSpacer = "\u202F";

  const coordinateUnit = getCoordinateUnit(distanceUnit);

  const parts = coords.map((num, index) => {
    const formatted = formatDistanceDecimal(num, coordinateUnit, true);

    // Return formatted label + value with narrow spacing around colon
    return `${labels[index] ?? "?"}${labelSpacer}:${labelSpacer}${formatted}`;
  });

  // Join the parts with multiple non-breaking spaces for consistent visual spacing
  return parts.join("\u00A0\u00A0\u00A0");
}

/**
 * Returns the coordinate unit to use for display based on the selected distance unit.
 * For inches, feet is used for coordinate display.
 *
 * @param distanceUnit The selected distance unit.
 * @returns The coordinate unit to use for display.
 */
export function getCoordinateUnit(
  distanceUnit: DistanceUnitTypes,
): DistanceUnitTypes {
  if (
    distanceUnit === DistanceUnitTypes.fractionalInches ||
    distanceUnit === DistanceUnitTypes.decimalInches
  ) {
    return DistanceUnitTypes.decimalFeet;
  }
  return distanceUnit;
}

/**
 * @returns a context menu for the 3D Scene
 */
export function SceneContextMenu(): JSX.Element {
  const dispatch = useAppDispatch();
  const [contextMenu, setContextMenu] = useState<SceneContextMenuEvent>();

  const [coordinates, setCoordinates] = useState<Vector3Tuple | undefined>();
  const [objectId, setObjectId] = useState<number | undefined>();

  const cadSceneEvents = useSceneEvents();

  const { openToast } = useToast();

  useTypedEvent<SceneContextMenuEvent>(
    cadSceneEvents.openSceneContextMenuFrom3DView,
    (event) => {
      cadSceneEvents.locateCadObjectInTree.emit(event.objectId);
      setContextMenu({ ...event });
      setCoordinates(event.point);
      setObjectId(event.objectId);
    },
  );

  const closeContextMenu = useCallback(() => {
    setContextMenu(undefined);
  }, []);

  const hideCadObject = useCallback(() => {
    if (!contextMenu || contextMenu.objectId === undefined) return;
    Analytics.track<HideCadElementEventProperties>(EventType.hideCadElement, {
      via: "contextMenu",
    });
    cadSceneEvents.setCadModelVisibility.emit({
      objectId: contextMenu.objectId,
      visibility: false,
      isolate: false,
    });

    closeContextMenu();
  }, [cadSceneEvents, contextMenu, closeContextMenu]);

  const isolateCadObject = useCallback(() => {
    if (!contextMenu || contextMenu.objectId === undefined) return;
    Analytics.track<IsolateCadElementEventProperties>(
      EventType.isolateCadElement,
      {
        via: "contextMenu",
        visibility: true,
      },
    );
    cadSceneEvents.setCadModelVisibility.emit({
      objectId: contextMenu.objectId,
      visibility: true,
      isolate: true,
    });

    closeContextMenu();
  }, [cadSceneEvents, contextMenu, closeContextMenu]);

  const selectParent = useCallback(() => {
    if (!contextMenu || contextMenu.objectId === undefined) return;
    Analytics.track<SelectCadParentElementEventProperties>(
      EventType.selectCadParentElement,
      {
        via: "contextMenu",
      },
    );
    cadSceneEvents.selectParentCadObjectInTree.emit(contextMenu.objectId);
    closeContextMenu();
  }, [cadSceneEvents, contextMenu, closeContextMenu]);

  const [sparseCoordinateString, setSparseCoordinateString] =
    useState<string>();

  const distanceUnit = useDistanceUnit();
  const coordinateString = useMemo(() => {
    if (coordinates) {
      const coordStr = getCoordinateStringZUp(coordinates, distanceUnit, false);
      setSparseCoordinateString(
        formatSparseCoordinateString(coordinates, distanceUnit),
      );
      return coordStr;
    }
  }, [coordinates, distanceUnit]);

  const copyCoordinates = useCallback((): void => {
    if (coordinateString) {
      Analytics.track<CopyCoordinateProperties>(EventType.copyCoordinate, {
        via: "context menu",
      });

      // Copy to the clipboard.
      navigator.clipboard.writeText(coordinateString);
      openToast({ title: "Coordinates copied to clipboard" });
      closeContextMenu();
    }
  }, [closeContextMenu, coordinateString, openToast]);

  const placePinMarker = useCallback((): void => {
    if (coordinates) {
      Analytics.track<PlacePinProperties>(EventType.placePin, {
        placementMethod: "contextMenu",
      });
      dispatch(setPinMarker(coordinates));
      closeContextMenu();
    }
  }, [closeContextMenu, coordinates, dispatch]);

  useTypedEvent<PlacePinEvent>(cadSceneEvents.placePinMarker, (event) => {
    Analytics.track<PlacePinProperties>(EventType.placePin, {
      placementMethod: "shortcut",
    });
    dispatch(setPinMarker(event.point));
  });

  return (
    <>
      {contextMenu?.panoDepthInfo &&
        !contextMenu.panoDepthInfo.isPanoWithDepth && <PanoNoDepthInfoToast />}
      {contextMenu?.panoDepthInfo?.isPanoWithDepth && (
        <WalkDepthLoadingToast
          isDepthReady={contextMenu.panoDepthInfo.isDepthReady}
        />
      )}
      {contextMenu && !contextMenu.panoDepthInfo && (
        <SceneContextMenuBase
          onClose={closeContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={{ top: contextMenu.y, left: contextMenu.x }}
        >
          {objectId !== undefined && (
            <FaroMenuItem
              dark
              onClick={hideCadObject}
              label="Hide"
              Icon={NonVisibleIcon}
            />
          )}
          {objectId !== undefined && (
            <FaroMenuItem
              dark
              onClick={isolateCadObject}
              label="Isolate"
              Icon={VisibleIcon}
            />
          )}
          {objectId !== undefined && (
            <FaroMenuItem
              dark
              onClick={selectParent}
              label="Select parent"
              shortcut="Tab"
              Icon={FolderSmallIcon}
            />
          )}
          {coordinateString && (
            <FaroMenuItem
              dark
              onClick={copyCoordinates}
              label={sparseCoordinateString}
              Icon={WorldCoordinateIcon}
            />
          )}
          {coordinates && (
            <FaroMenuItem
              dark
              onClick={placePinMarker}
              label="Place pin"
              shortcut="Shift+right-click"
              Icon={PinIcon}
            />
          )}
        </SceneContextMenuBase>
      )}
    </>
  );
}

type SceneContextMenuBaseProps = Omit<
  FaroMenuProps,
  "hideBackdrop" | "open" | "dark" | "slotProps" | "onClose" | "onContextMenu"
> & {
  /** Callback when the menu should be closed */
  onClose(): void;
};

/**
 * @returns Base component to implement a context menu in the 3D scene.
 * Unlike a normal FaroMenu, it is non-modal and allows other context menus to be opened while it is open.
 */
export function SceneContextMenuBase({
  children,
  onClose,
  sx,
  ...faroMenuProps
}: PropsWithChildren<SceneContextMenuBaseProps>): JSX.Element {
  return (
    <ClickAwayListener
      onClickAway={onClose}
      // Using onPointerDown here, to make sure we close the menu on right clicks as well, and additionally,
      // that before moving to a new position, the context menu is remounted fully.
      mouseEvent="onPointerDown"
    >
      <FaroMenu
        open
        dark
        onClose={onClose}
        // Stop pointer events from stopping at the backdrop, and allow click events to propagate to the ClickAwayListener instead.
        sx={[{ pointerEvents: "none" }, ...(Array.isArray(sx) ? sx : [sx])]}
        slotProps={{ paper: { sx: { pointerEvents: "auto" } } }}
        // Prevent default browser context menu.
        // Note: Without following line, on Windows Chrome/Edge always popup the default browser
        // context menu at the same time and often block this menu. There seems be no such issue
        // for Chrome/Edge/Firefox on Mac.
        onContextMenu={(e) => e.preventDefault()}
        {...faroMenuProps}
      >
        {children}
      </FaroMenu>
    </ClickAwayListener>
  );
}
